import { Component, OnInit, ViewChild, OnDestroy, ElementRef } from '@angular/core';
import { WorkflowTemplate } from 'app/models/workflow-template';
import { WorkflowTemplateService } from 'app/services/workflow-template.service';
import { map, tap, switchMap, startWith, filter, take, finalize } from 'rxjs/operators';
import { Team } from 'app/models/team';
import { User } from 'app/models/user';
import { UserService } from 'app/services/user.service';
import { TeamService } from 'app/services/team.service';
import { DatatableComponent } from '@swimlane/ngx-datatable';
import { WorkflowWizardService } from 'app/services/workflow-wizard.service';
import { Tag } from 'app/models/tag';
import { TagService } from 'app/services/tag.service';
import * as _ from "lodash";
import { Subscription, Subject } from 'rxjs';
import { FormGroup, AbstractControl } from '@angular/forms';
import { ValidationHandler } from 'app/helpers/validation.handler';
import { ModalDirective } from 'ng-uikit-pro-standard';
import { WorkflowStepCompletion } from 'app/models/workflow-step-completion';
import { EcoDocsTitleService } from 'app/services/ecodocs-title.service';

enum filterActiveType {
  ALL = 1,
  ACTIVE,
  INACTIVE,
  DRAFTS
}

@Component({
  selector: 'app-templates',
  templateUrl: './templates.component.html',
  styleUrls: ['./templates.component.scss']
})
export class TemplatesComponent implements OnInit, OnDestroy {

  filterActiveOptions = [
    { value: filterActiveType.ALL, label: 'Any', selected:true},
    { value: filterActiveType.ACTIVE, label: 'Active Only'},
    { value: filterActiveType.INACTIVE, label: 'Inactive Only'},
    { value: filterActiveType.DRAFTS, label: 'Drafts Only'}
  ];
  filterActive: filterActiveType = filterActiveType.ALL;

  tags: Tag[] = [];
  tagsSubscription: Subscription = null;

  filterAuthorId: number = null;
  filterTags: Tag[] = [];

  unfileteredWorkflows: WorkflowTemplate[];
  workflows: WorkflowTemplate[];

  tableLoadingIndicator: boolean = true;
  isLoadingVersionHistory: boolean = false;

  currentWorkflowTemplateHistory: WorkflowTemplate[] = [];
  versionHistoryTitle: string = '';

  tableLimitSelect: Array<any>;
  selectedTableLimit: number = 5;
  
  loadingWorkflowIds:number[] = [];

  workflowsFilter: string = null;

  users: User[] = [];

  cloneWorkflowSubmitSubject: Subject<string>;
  cloneWorkflowFormGroup: FormGroup = null;
  isCloningInProgress: boolean = false;

  @ViewChild(DatatableComponent, { static: true }) table: DatatableComponent;
  @ViewChild('cloneWorkflowModal') cloneWorkflowModal: ModalDirective;
  @ViewChild('cloneNewNameInput', {static: false}) cloneNewNameInput: ElementRef;
  @ViewChild('versionHistoryModal') versionHistoryModal: ModalDirective;

  constructor(private workflowTemplateService: WorkflowTemplateService, private workflowWizardService: WorkflowWizardService, 
              public userService: UserService, public tagService: TagService, private _validationHandler: ValidationHandler
              , private ecoDocsTitleService: EcoDocsTitleService) { 
    
  }

  generateNew(){
    this.workflowWizardService.init(this.workflowTemplateService.workflowTemplate());
  }

  ngOnInit() {

    this.ecoDocsTitleService.setTitle( 'Workflow Rules', false );
    this.filterAuthorId = null;
    this.filterTags = [];
    
      
    this.loadingWorkflowIds = [];
  
    this.workflowsFilter = null;

    this.cloneWorkflowFormGroup = null;
    this.isCloningInProgress = false;

    this.refreshWorkflows();
    
    this.tableLimitSelect = [5, 10, 20, 30, 50, 100];
    this.selectedTableLimit = 5;

    this.userService.getAll().subscribe(u => { this.users = u; });

    this.tagsSubscription = this.tagService.currentTagsSubject.subscribe(t => this.tags = t);
  }

  ngOnDestroy() {
    if (this.tagsSubscription) {
      this.tagsSubscription.unsubscribe();
      this.tagsSubscription = null;
    }
  }

  refreshWorkflows() {
    this.tableLoadingIndicator = true;
    this.workflowTemplateService.getAll(true).subscribe(workflowTemplates => {
      this.workflows = workflowTemplates;
      this.unfileteredWorkflows = [...workflowTemplates];

      setTimeout(() => { this.tableLoadingIndicator = false; }, 100);
    });
  }

  showCloneWorkflowModal(idToClone: number) {
    this.cloneWorkflowFormGroup = this.workflowTemplateService.workflowTemplateFormGroup(this.workflowTemplateService.workflowTemplate());
    
    this.cloneWorkflowSubmitSubject = new Subject();
    this.cloneWorkflowSubmitSubject.pipe(
      tap(()=> { 
          // Validate only general settings
          this.cloneWorkflowFormGroup.controls['initial_step_completion'] = new FormGroup({});
          this.cloneWorkflowFormGroup.controls['initial_step_completion'].updateValueAndValidity();

          this._validationHandler.onSubmitCheck(this.cloneWorkflowFormGroup);
        }),
      switchMap(() =>
        this.cloneWorkflowFormGroup.controls['name'].statusChanges.pipe(
          startWith(this.cloneWorkflowFormGroup.controls['name'].status),
          filter(status => status !== 'PENDING'),
          take(1)
        )
      ),
    ).subscribe((status) => {
      if (status == 'VALID') {
        this.cloneWorkflow(idToClone);
      } else {
        // not valid
      }
    });
    this.cloneWorkflowModal.show();
  }

  removeIdsRecursive(stepCompletion: WorkflowStepCompletion) {
    stepCompletion.id = null;
    stepCompletion.step_id = null;
    stepCompletion.next_step_id = null;

    stepCompletion.files.forEach((f) => {f.id = null;});
    stepCompletion.forms.forEach((f) => {f.id = null;});
    stepCompletion.auto_emails.forEach((e) => {e.id = null;});
    stepCompletion.auto_uploads.forEach((a) => {a.id = null;});
    
    if (stepCompletion.next_step) {
      stepCompletion.next_step.id = null;
      stepCompletion.next_step.target_scope_id = null;
      stepCompletion.next_step.step_completions.forEach((sc) => {
        this.removeIdsRecursive(sc);
      });
    }
  }

  cloneWorkflow(idToClone: number) {
    this.workflowTemplateService.getById(idToClone).subscribe((w) => {
      // Remove all IDs to create a new one
      w.id = null;
      w.name = this.cloneWorkflowFormGroup.value.name;
      w.observers_scope_id = null;
      w.initial_step_completion_id = null;

      this.removeIdsRecursive(w.initial_step_completion);
      this.workflowTemplateService.put(w).pipe(finalize(()=>{
        this.isCloningInProgress = false;
        this.cloneWorkflowModal.hide();
        this.cloneWorkflowFormGroup = null;
        this.refreshWorkflows();
      })).subscribe();
    });
  }

  innerWidth() {
    return window.innerWidth;
  }

  onActivateChanged(event, workflowTemplate) {
    if (event.target.checked) {
      if (!this.loadingWorkflowIds.includes(workflowTemplate.id)) {
        this.workflowTemplateService.restore(workflowTemplate.id).pipe(map(x => {
          /** @todo handle error */
          workflowTemplate.deleted_at = null;
          this.loadingWorkflowIds = this.loadingWorkflowIds.filter(id => id != workflowTemplate.id);
          this.workflows = [...this.workflows];
        })).subscribe();
        this.loadingWorkflowIds.push(workflowTemplate.id);
      }
    } else {
      if (!this.loadingWorkflowIds.includes(workflowTemplate.id)) {
        this.workflowTemplateService.deactivate(workflowTemplate.id).subscribe(x => {
          /** @todo handle error */
          workflowTemplate.deleted_at = new Date();
          this.loadingWorkflowIds = this.loadingWorkflowIds.filter(id => id != workflowTemplate.id);
          this.workflows = [...this.workflows];
        });
        this.loadingWorkflowIds.push(workflowTemplate.id);
      }
    }
  }

  updateFilter() {
    const val = (this.workflowsFilter)? this.workflowsFilter.toLowerCase(): null;
    
    // filter our data
    const temp = this.unfileteredWorkflows.filter((workflow) => {
      
              // Author filter
      return  (this.filterAuthorId == null || this.filterAuthorId == workflow.author_user.id) &&
              // Tags filter
              (this.filterTags.length == 0 || _.intersectionWith(this.filterTags, workflow.tags, (a, b)=>{return a.name == b.name}).length > 0) &&
              // Search filter
              (workflow.name.toLowerCase().indexOf(val) !== -1 || (workflow.description && workflow.description.toLowerCase().indexOf(val) !== -1) || !val) &&
              // Active filter
              (this.filterActive == filterActiveType.ALL || (!workflow.is_draft && (this.filterActive == filterActiveType.ACTIVE && !workflow.deleted_at) || (this.filterActive == filterActiveType.INACTIVE && workflow.deleted_at)) || (this.filterActive == filterActiveType.DRAFTS && workflow.is_draft));
    });

    // update the rows
    this.workflows = temp;
    
    // Whenever the filter changes, always go back to the first page
    this.table.offset = 0;
  }

  loadVersionHistory(workflowTemplate: WorkflowTemplate) {
    this.currentWorkflowTemplateHistory = [];
    this.isLoadingVersionHistory = true;
    this.versionHistoryModal.show();
    this.versionHistoryTitle = workflowTemplate.name;
    this.workflowTemplateService.listVersions(workflowTemplate.id).pipe(finalize(()=>{
      this.isLoadingVersionHistory = false;
    })).subscribe((w)=>{
      this.currentWorkflowTemplateHistory = w;
    });
  }

  onRestoreVersion(workflowTemplate: WorkflowTemplate) {
    this.isLoadingVersionHistory = true;
    this.workflowTemplateService.restoreVersion(workflowTemplate.id, workflowTemplate.version).pipe(finalize(() => {
      this.isLoadingVersionHistory = false;
      this.loadVersionHistory(workflowTemplate);
      this.refreshWorkflows();
    })).subscribe();
  }
}
